import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, AutoComplete, Input, List, Typography, message, Space, Tag, Dropdown, Button, Spin, Progress, Alert, Steps } from 'antd';
import { CopyOutlined, DatabaseOutlined, TableOutlined, FieldBinaryOutlined, DownOutlined, SwapOutlined, CloudSyncOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import useFetchDashboardStructure from '../functions/FetchDashboardStructure';
import jwtDecode from 'jwt-decode';

const { Text, Title } = Typography;
const { confirm } = Modal;

// DAS-1844: Detect environment (PROD vs DEV)
const isProdEnvironment = () => {
  const hostname = window.location.hostname;
  return hostname.includes('monosoft.online') || hostname.includes('37.97.140.217');
};

const DatabaseSchemaSearch = ({ isVisible, onClose }) => {
  const { allDatabasesStructure, loading, error, databaseErrors } = useFetchDashboardStructure(isVisible);
  const [searchValue, setSearchValue] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  
  // DAS-1844: Sync state
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncStage, setSyncStage] = useState('');
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [syncEstimate, setSyncEstimate] = useState(null);
  const [syncDirectionDisplay, setSyncDirectionDisplay] = useState({ source: '', dest: '' });
  
  // DAS-1853: Pre-sync comparison state
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  
  // DAS-1844: Table comparison state (simple PROD<>DEV comparison)
  const [tableCompareData, setTableCompareData] = useState(null);
  const [tableCompareLoading, setTableCompareLoading] = useState(false);
  const [showTableCompare, setShowTableCompare] = useState(false);

  // DAS-2320: PROD-dump-install-on-DEV state
  const [dbCopiesInfo, setDbCopiesInfo] = useState(null);        // { dashboard: {...}, correlation_research: {...} }
  const [dbCopiesLoading, setDbCopiesLoading] = useState(false);
  const [dbCopiesError, setDbCopiesError] = useState(null);
  const [installJob, setInstallJob] = useState(null);            // { job_id, db_name, state, progress_pct, message, error }
  // DAS-2320 ext: PROD freshness + one-click pull
  const [prodDumpsInfo, setProdDumpsInfo] = useState(null);      // { dashboard: {...prod_fresher...}, correlation_research: {...} }
  const [prodFreshnessLoading, setProdFreshnessLoading] = useState(false);
  const [prodFreshnessError, setProdFreshnessError] = useState(null);

  // Determine current environment and target
  // IMPORTANT: Sync can only be initiated FROM DEV because:
  // - DEV can SSH to PROD (PROD has public IP)
  // - PROD cannot SSH to DEV (DEV is behind NAT, no public IP)
  const isProd = isProdEnvironment();
  const currentEnv = isProd ? 'PROD' : 'DEV';
  const targetEnv = isProd ? 'DEV' : 'PROD';
  const syncDirection = isProd ? 'prod_to_dev' : 'dev_to_prod';
  const canSync = !isProd; // Only DEV can initiate syncs

  // DAS-1844: Check token validity (must have > 2 hours remaining)
  const checkTokenValidity = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return { valid: false, message: 'No token found. Please login first.' };
    
    try {
      const decoded = jwtDecode(token);
      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const remainingMs = expiryTime - now;
      const remainingHours = remainingMs / (1000 * 60 * 60);
      
      if (remainingHours < 2) {
        return { 
          valid: false, 
          message: `Token expires in ${remainingHours.toFixed(1)} hours. Please logout and login again to refresh your token (needs > 2 hours).` 
        };
      }
      return { valid: true, remainingHours };
    } catch (e) {
      return { valid: false, message: 'Invalid token. Please login again.' };
    }
  }, []);

  // DAS-1844: Get estimate for sync
  const getEstimate = useCallback(async (database, table) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/db-sync/estimate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ database, table })
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error('Estimate error:', e);
      return { success: false, error: e.message };
    }
  }, []);

  // DAS-1844: Check target availability
  const checkTargetAvailability = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/db-sync/check-target', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction: syncDirection })
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error('Check target error:', e);
      return { success: false, available: false, error: e.message };
    }
  }, [syncDirection]);

  // DAS-1844: Execute sync
  const executeSync = useCallback(async (database, table) => {
    const token = localStorage.getItem('token');
    setSyncInProgress(true);
    setSyncStage('executing');
    setSyncProgress(10);
    setSyncError(null);
    
    try {
      const response = await fetch('/api/db-sync/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction: syncDirection, database, table })
      });
      const data = await response.json();
      
      if (data.success) {
        setSyncProgress(100);
        setSyncStage('completed');
        message.success(`Successfully synced ${database}${table ? '.' + table : ''} to ${targetEnv}!`);
      } else {
        setSyncError(data.message || data.error || 'Sync failed');
        setSyncStage('failed');
      }
      return data;
    } catch (e) {
      console.error('Sync error:', e);
      setSyncError(e.message);
      setSyncStage('failed');
      return { success: false, error: e.message };
    } finally {
      setSyncInProgress(false);
    }
  }, [syncDirection, targetEnv]);

  // DAS-1853: Preview comparison before sync
  const handlePreviewClick = useCallback(async (direction) => {
    if (!selectedResult || (selectedResult.type !== 'database' && selectedResult.type !== 'table')) {
      message.warning('Please select a database or table first');
      return;
    }

    const tokenCheck = checkTokenValidity();
    if (!tokenCheck.valid) {
      Modal.error({ title: 'Token Expired', content: tokenCheck.message });
      return;
    }

    setComparisonLoading(true);
    setShowComparison(true);
    setComparisonData(null);

    try {
      const token = localStorage.getItem('token');
      const database = selectedResult.type === 'database' 
        ? selectedResult.fullPath 
        : selectedResult.fullPath.split('.')[0];
      
      const response = await fetch('/api/db-sync/compare', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction, database })
      });
      const data = await response.json();
      
      if (data.success) {
        setComparisonData({ ...data, direction });
      } else {
        message.error(data.error || 'Failed to compare databases');
        setShowComparison(false);
      }
    } catch (e) {
      console.error('Compare error:', e);
      message.error(e.message);
      setShowComparison(false);
    } finally {
      setComparisonLoading(false);
    }
  }, [selectedResult, checkTokenValidity]);

  // DAS-1844: Handle table comparison (PROD<>DEV) - simple size/rows comparison
  const handleTableCompare = useCallback(async () => {
    if (!selectedResult || selectedResult.type !== 'table') {
      message.warning('Please select a table first');
      return;
    }

    const tokenCheck = checkTokenValidity();
    if (!tokenCheck.valid) {
      Modal.error({ title: 'Token Expired', content: tokenCheck.message });
      return;
    }

    setTableCompareLoading(true);
    setShowTableCompare(true);
    setTableCompareData(null);

    try {
      const token = localStorage.getItem('token');
      const database = selectedResult.databaseName;
      const table = selectedResult.display;
      
      const response = await fetch('/api/db-sync/compare-table', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ database, table })
      });
      const data = await response.json();
      
      if (data.success) {
        setTableCompareData(data);
      } else {
        message.error(data.error || 'Failed to compare table');
        setShowTableCompare(false);
      }
    } catch (e) {
      console.error('Table compare error:', e);
      message.error(e.message);
      setShowTableCompare(false);
    } finally {
      setTableCompareLoading(false);
    }
  }, [selectedResult, checkTokenValidity]);

  // ============================================================================
  // DAS-2320: PROD-dump-install-on-DEV (list + install + poll)
  // ============================================================================

  const fetchDbCopies = useCallback(async () => {
    setDbCopiesLoading(true);
    setDbCopiesError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/db-copies/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      setDbCopiesInfo(data.dumps || {});
    } catch (err) {
      setDbCopiesError(err.message);
    } finally {
      setDbCopiesLoading(false);
    }
  }, []);

  // Fetch on modal open (DEV only — endpoint 503s on PROD)
  useEffect(() => {
    if (isVisible && !isProd) {
      fetchDbCopies();
    }
  }, [isVisible, isProd, fetchDbCopies]);

  // DAS-2320 ext: fetch PROD freshness on modal open too
  const fetchProdFreshness = useCallback(async () => {
    setProdFreshnessLoading(true);
    setProdFreshnessError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/db-copies/prod-freshness', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      setProdDumpsInfo(data.prod_dumps || {});
    } catch (err) {
      setProdFreshnessError(err.message);
    } finally {
      setProdFreshnessLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isVisible && !isProd) {
      fetchProdFreshness();
    }
  }, [isVisible, isProd, fetchProdFreshness]);

  const handlePullFromProdClick = useCallback(() => {
    confirm({
      title: 'Pull latest PROD dumps to DEV?',
      icon: <CloudSyncOutlined style={{ color: '#1890ff' }} />,
      content: (
        <div>
          <p>This will SSH into PROD, fetch the latest <code>Full_Production_Backup_*.sql</code> and <code>correlation_research_Production_Backup_*.sql</code>, and replace the current local copies in <code>dashboard/dumps/</code>.</p>
          <p><strong>Transfer size:</strong> ~12 GB total. Typical duration: 8–15 minutes depending on connection speed.</p>
          <p>This does <em>not</em> install the dumps into MySQL — it only refreshes the local <code>.sql</code> files. After the pull completes, use "Install on DEV" to apply them.</p>
        </div>
      ),
      okText: 'Yes, pull now',
      okType: 'primary',
      cancelText: 'Cancel',
      width: 600,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/db-copies/pull-from-prod', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || `HTTP ${response.status}`);
          }
          setInstallJob({
            job_id: data.job_id,
            db_name: '*pull*',
            state: 'pending',
            progress_pct: 0,
            message: 'Starting PROD pull...',
          });
          message.info('Pulling dumps from PROD...');
        } catch (err) {
          Modal.error({ title: 'Pull failed to start', content: err.message });
        }
      },
    });
  }, []);

  // Poll install status when a job is active
  useEffect(() => {
    if (!installJob || !installJob.job_id) return;
    if (installJob.state === 'done' || installJob.state === 'failed') return;

    const token = localStorage.getItem('token');
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/db-copies/install-status/${installJob.job_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.job) {
          const wasActive = installJob.state !== 'done' && installJob.state !== 'failed';
          const nowDone = data.job.state === 'done' || data.job.state === 'failed';
          setInstallJob(prev => ({ ...prev, ...data.job }));
          // DAS-2320 ext: after a successful PROD pull, refresh local + prod-freshness lists
          // so the dump timestamps update and "PROD has newer" banner clears.
          if (wasActive && nowDone && installJob.db_name === '*pull*' && data.job.state === 'done') {
            fetchDbCopies();
            fetchProdFreshness();
          }
        }
      } catch (e) {
        // ignore transient errors; keep polling
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [installJob, fetchDbCopies, fetchProdFreshness]);

  const handleInstallDumpClick = useCallback((dbName) => {
    const info = dbCopiesInfo?.[dbName];
    if (!info) {
      message.error(`No dump available for ${dbName}`);
      return;
    }

    confirm({
      title: `Install PROD copy of '${dbName}' into DEV?`,
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p><strong>This will replace your local <code>{dbName}</code> database with a copy from PROD.</strong></p>
          <p>Any local changes you made to <code>{dbName}</code> since the last PROD pull will be permanently lost.</p>
          <p>
            <strong>File:</strong> <code>{info.filename}</code><br/>
            <strong>Size:</strong> {info.size_human}<br/>
            <strong>Taken on PROD:</strong> {new Date(info.timestamp_iso).toLocaleString()} ({info.age_days} days ago)<br/>
            <strong>Expected duration:</strong> ~{dbName === 'dashboard' ? '5–8' : '15–30'} minutes
          </p>
          <p>The swap is atomic — DEV MySQL stays usable on the old data until the moment of the swap.</p>
        </div>
      ),
      okText: 'Yes, install',
      okType: 'danger',
      cancelText: 'Cancel',
      width: 600,
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/db-copies/install', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ db_name: dbName }),
          });
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || `HTTP ${response.status}`);
          }
          setInstallJob({
            job_id: data.job_id,
            db_name: dbName,
            state: 'pending',
            progress_pct: 0,
            message: 'Starting...',
          });
          message.info(`Install started for ${dbName}`);
        } catch (err) {
          Modal.error({ title: 'Install failed to start', content: err.message });
        }
      },
    });
  }, [dbCopiesInfo]);
  // direction param: 'dev_to_prod' or 'prod_to_dev'
  const handleSyncClick = useCallback(async (direction = syncDirection) => {
    if (!selectedResult || (selectedResult.type !== 'database' && selectedResult.type !== 'table')) {
      message.warning('Please select a database or table first');
      return;
    }

    // Step 1: Check token validity
    const tokenCheck = checkTokenValidity();
    if (!tokenCheck.valid) {
      Modal.error({
        title: 'Token Expired',
        content: tokenCheck.message,
        okText: 'OK'
      });
      return;
    }

    const database = selectedResult.type === 'database' ? selectedResult.display : selectedResult.databaseName;
    const table = selectedResult.type === 'table' ? selectedResult.display : null;
    const itemName = table ? `${database}.${table}` : database;
    
    // Determine source/target based on direction
    const sourceEnv = direction === 'prod_to_dev' ? 'PROD' : 'DEV';
    const destEnv = direction === 'prod_to_dev' ? 'DEV' : 'PROD';

    // Step 2: Check target availability
    message.loading({ content: 'Checking connectivity...', key: 'sync-check' });
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/db-sync/check-target', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction })
      });
      const availability = await response.json();
      message.destroy('sync-check');
      
      if (!availability.available) {
        Modal.error({
          title: 'Target Unavailable',
          content: availability.message || `Cannot connect. Please check the server is running.`,
          okText: 'OK'
        });
        return;
      }
    } catch (e) {
      message.destroy('sync-check');
      Modal.error({
        title: 'Connection Error',
        content: e.message,
        okText: 'OK'
      });
      return;
    }

    // Step 3: Get estimate
    message.loading({ content: 'Calculating size...', key: 'sync-estimate' });
    const estimate = await getEstimate(database, table);
    message.destroy('sync-estimate');
    
    if (!estimate.success) {
      Modal.error({
        title: 'Estimation Failed',
        content: estimate.error || 'Could not estimate size',
        okText: 'OK'
      });
      return;
    }
    setSyncEstimate(estimate);

    // Step 4: Confirmation
    confirm({
      title: `Copy ${itemName} from ${sourceEnv} to ${destEnv}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Source:</strong> {sourceEnv}</p>
          <p><strong>Target:</strong> {destEnv}</p>
          <p><strong>Size:</strong> {estimate.size_human}</p>
          <p><strong>Rows:</strong> {estimate.row_count?.toLocaleString() || 'N/A'}</p>
          <p><strong>Estimated time:</strong> {estimate.duration_human}</p>
          <Alert 
            type="warning" 
            message={`This will replace existing data on ${destEnv}!`}
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Yes, Start Sync',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        // Execute with the specific direction
        setSyncInProgress(true);
        setSyncStage('executing');
        setSyncProgress(10);
        setSyncError(null);
        setSyncDirectionDisplay({ source: sourceEnv, dest: destEnv });
        
        try {
          const response = await fetch('/api/db-sync/execute', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ direction, database, table })
          });
          const data = await response.json();
          
          if (data.success) {
            setSyncProgress(100);
            setSyncStage('completed');
            message.success(`Successfully synced ${itemName} from ${sourceEnv} to ${destEnv}!`);
          } else {
            setSyncError(data.message || data.error || 'Sync failed');
            setSyncStage('failed');
          }
        } catch (e) {
          setSyncError(e.message);
          setSyncStage('failed');
        } finally {
          setSyncInProgress(false);
        }
      }
    });
  }, [selectedResult, checkTokenValidity, getEstimate, syncDirection]);

  // Flatten structure into searchable items (DAS-1473: support multiple databases)
  const searchableItems = useMemo(() => {
    if (!allDatabasesStructure) return [];

    const items = [];
    
    // Add databases
    Object.keys(allDatabasesStructure).forEach(dbName => {
      items.push({
        type: 'database',
        value: dbName,
        label: dbName,
        display: dbName,
        fullPath: dbName
      });

      const dbStructure = allDatabasesStructure[dbName];
      
      // Add tables
      Object.keys(dbStructure).forEach(tableName => {
        items.push({
          type: 'table',
          value: tableName,
          label: `${dbName}.${tableName}`,
          display: tableName,
          fullPath: `${dbName}.${tableName}`,
          databaseName: dbName
        });

        // Add fields
        if (dbStructure[tableName] && Array.isArray(dbStructure[tableName])) {
          dbStructure[tableName].forEach(field => {
            items.push({
              type: 'field',
              value: field.name,
              label: `${dbName}.${tableName}.${field.name}`,
              display: field.name,
              fullPath: `${dbName}.${tableName}.${field.name}`,
              tableName: tableName,
              databaseName: dbName,
              fieldType: field.type,
              isPrimaryKey: field.primary_key
            });
          });
        }
      });
    });

    return items;
  }, [allDatabasesStructure]);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) return [];

    const searchLower = searchValue.toLowerCase();
    const matches = searchableItems.filter(item => {
      const matchesValue = item.value.toLowerCase().includes(searchLower);
      const matchesLabel = item.label.toLowerCase().includes(searchLower);
      const matchesDisplay = item.display.toLowerCase().includes(searchLower);
      const matchesFullPath = item.fullPath.toLowerCase().includes(searchLower);
      
      return matchesValue || matchesLabel || matchesDisplay || matchesFullPath;
    });
    
    // Sort results: exact matches first, then by type (database > table > field), then alphabetically
    matches.sort((a, b) => {
      const aExact = a.fullPath.toLowerCase() === searchLower || a.label.toLowerCase() === searchLower;
      const bExact = b.fullPath.toLowerCase() === searchLower || b.label.toLowerCase() === searchLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Exact display match (e.g., "ticker" matches table "ticker" exactly)
      const aDisplayExact = a.display.toLowerCase() === searchLower.split('.').pop();
      const bDisplayExact = b.display.toLowerCase() === searchLower.split('.').pop();
      if (aDisplayExact && !bDisplayExact) return -1;
      if (!aDisplayExact && bDisplayExact) return 1;
      
      // Type priority: database > table > field
      const typePriority = { database: 0, table: 1, field: 2 };
      if (typePriority[a.type] !== typePriority[b.type]) {
        return typePriority[a.type] - typePriority[b.type];
      }
      
      // Alphabetical
      return a.label.localeCompare(b.label);
    });
    
    return matches.slice(0, 50); // Limit to 50 results
  }, [searchValue, searchableItems]);

  // AutoComplete options
  const autocompleteOptions = useMemo(() => {
    return filteredItems.map(item => ({
      value: item.fullPath,
      label: (
        <Space>
          {item.type === 'database' && <DatabaseOutlined />}
          {item.type === 'table' && <TableOutlined />}
          {item.type === 'field' && <FieldBinaryOutlined />}
          <Text strong={item.type === 'database'}>{item.label}</Text>
          {item.type === 'field' && item.isPrimaryKey && (
            <Tag color="gold" size="small">PK</Tag>
          )}
        </Space>
      ),
      item: item
    }));
  }, [filteredItems]);

  // Copy to clipboard function
  const copyToClipboard = (text, successMsg = 'Copied to clipboard!') => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success(successMsg);
      })
      .catch(() => {
        message.error('Failed to copy!');
      });
  };

  // Generate table structure description (like DESCRIBE TABLE)
  const generateTableStructure = (result) => {
    if (!result || result.type !== 'table' || !allDatabasesStructure) return '';
    
    const dbName = result.databaseName;
    const tableName = result.display;
    const dbStructure = allDatabasesStructure[dbName];
    
    if (!dbStructure || !dbStructure[tableName]) return '';
    
    const columns = dbStructure[tableName];
    let structure = `Table: ${dbName}.${tableName}\n`;
    structure += `${'='.repeat(80)}\n`;
    structure += `${'Field'.padEnd(30)} ${'Type'.padEnd(30)} ${'Key'.padEnd(10)}\n`;
    structure += `${'-'.repeat(80)}\n`;
    
    columns.forEach(col => {
      const field = col.name.padEnd(30);
      const type = String(col.type).padEnd(30);
      const key = col.primary_key ? 'PRI' : ''.padEnd(10);
      structure += `${field} ${type} ${key}\n`;
    });
    
    return structure;
  };

  // Generate field description
  const generateFieldDescription = (result) => {
    if (!result || result.type !== 'field') return '';
    return `${result.databaseName}.${result.tableName}.${result.display} (${result.fieldType})`;
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchValue(value);
    setSelectedResult(null);
  };

  // Handle select from autocomplete
  const handleSelect = (value, option) => {
    setSelectedResult(option.item);
    setSearchValue(value);
  };

  // Get all results for display
  const displayResults = useMemo(() => {
    if (!searchValue.trim()) return [];
    
    // Group by type
    const grouped = {
      database: [],
      table: [],
      field: []
    };

    filteredItems.forEach(item => {
      grouped[item.type].push(item);
    });

    return grouped;
  }, [filteredItems, searchValue]);

  return (
    <Modal
      title={
        <Space>
          <DatabaseOutlined />
          <span>Find Databases, Tables, Fields</span>
        </Space>
      }
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {/* DAS-1844: Show info when on PROD */}
      {isProd && (
        <Alert
          type="info"
          message="Sync from DEV only"
          description="Database sync must be initiated from DEV. PROD cannot reach your local DEV machine."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* DAS-2320: Install PROD dumps on DEV (DEV-only) */}
      {!isProd && (
        <div style={{
          marginBottom: 16,
          padding: 16,
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          backgroundColor: '#fafafa',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Title level={5} style={{ margin: 0 }}>
              <CloudSyncOutlined /> Install PROD copy onto DEV
            </Title>
            <Button size="small" onClick={fetchDbCopies} disabled={dbCopiesLoading}>
              {dbCopiesLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </div>

          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Replaces the local <code>dashboard</code> or <code>correlation_research</code> database with the latest nightly backup pulled from PROD
            (script: <code>DR_pull_prod_backups_to_dev.py</code>, location: <code>dashboard/dumps/</code>).
          </Text>

          {dbCopiesError && (
            <Alert type="error" message={`Could not load dumps: ${dbCopiesError}`} style={{ marginBottom: 12 }} />
          )}

          {/* DAS-2320 ext: PROD-freshness banner + pull button */}
          {prodDumpsInfo && !prodFreshnessError && (() => {
            const anyFresher = ['dashboard', 'correlation_research'].some(
              k => prodDumpsInfo[k] && prodDumpsInfo[k].prod_fresher
            );
            const isPulling = installJob && installJob.db_name === '*pull*' && installJob.state !== 'done' && installJob.state !== 'failed';
            if (anyFresher) {
              return (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="PROD has newer dumps than DEV"
                  description={
                    <div>
                      {['dashboard', 'correlation_research'].map((k) => {
                        const p = prodDumpsInfo[k];
                        if (!p) return null;
                        if (!p.prod_fresher) return (
                          <div key={k} style={{ fontSize: 12 }}>• <code>{k}</code>: DEV is current</div>
                        );
                        return (
                          <div key={k} style={{ fontSize: 12 }}>
                            • <code>{k}</code>: PROD has <code>{p.filename}</code> ({new Date(p.timestamp_iso).toLocaleString()}),
                            DEV has {p.local_filename ? <>
                              <code>{p.local_filename}</code> ({new Date(p.local_timestamp_iso).toLocaleString()})
                            </> : 'no local copy'}
                          </div>
                        );
                      })}
                    </div>
                  }
                  action={
                    <Button size="small" type="primary" onClick={handlePullFromProdClick} disabled={isPulling} loading={isPulling}>
                      Pull latest from PROD
                    </Button>
                  }
                />
              );
            }
            return (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                message="DEV dumps are current"
                description={<div style={{ fontSize: 12 }}>Both PROD dumps match your local copies.</div>}
              />
            );
          })()}
          {prodFreshnessError && (
            <Alert type="warning" message={`Could not check PROD freshness: ${prodFreshnessError}`} style={{ marginBottom: 12 }} />
          )}

          {dbCopiesInfo && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {['dashboard', 'correlation_research'].map((dbName) => {
                const info = dbCopiesInfo[dbName];
                const isInstallingThis = installJob && installJob.db_name === dbName && installJob.state !== 'done' && installJob.state !== 'failed';
                return (
                  <div key={dbName} style={{
                    padding: 12,
                    border: '1px solid #e8e8e8',
                    borderRadius: 4,
                    backgroundColor: '#fff',
                  }}>
                    <Text strong>{dbName}</Text>
                    {info ? (
                      <>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          <div>📄 <code style={{ fontSize: 11 }}>{info.filename}</code></div>
                          <div>📦 {info.size_human}</div>
                          <div>🕒 {new Date(info.timestamp_iso).toLocaleString()} ({info.age_days}d ago)</div>
                        </div>
                        <Button
                          type="primary"
                          danger
                          size="small"
                          style={{ marginTop: 8 }}
                          onClick={() => handleInstallDumpClick(dbName)}
                          disabled={!!installJob && installJob.state !== 'done' && installJob.state !== 'failed'}
                          loading={isInstallingThis}
                        >
                          Install on DEV
                        </Button>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                        No dump found. Run <code>DR_pull_prod_backups_to_dev.py</code>.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {installJob && (
            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>
                  {installJob.state === 'done' ? '✅' : installJob.state === 'failed' ? '❌' : '⏳'} Install of <code>{installJob.db_name}</code>
                </Text>
                {installJob.duration_s != null && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({Math.round(installJob.duration_s)}s elapsed)
                  </Text>
                )}
              </div>
              <Progress
                percent={installJob.progress_pct || 0}
                status={installJob.state === 'failed' ? 'exception' : installJob.state === 'done' ? 'success' : 'active'}
              />
              <div style={{ marginTop: 8, fontSize: 13 }}>
                {installJob.message}
              </div>
              {installJob.tables_swapped > 0 && installJob.state === 'done' && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#52c41a' }}>
                  {installJob.tables_swapped} tables live
                </div>
              )}
              {installJob.error && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#ff4d4f' }}>
                  Error: {installJob.error}
                </div>
              )}
              {(installJob.state === 'done' || installJob.state === 'failed') && (
                <Button size="small" style={{ marginTop: 8 }} onClick={() => setInstallJob(null)}>
                  Dismiss
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, color: 'red' }}>
          Error loading database structure: {error}
        </div>
      )}

      {!loading && databaseErrors && Object.keys(databaseErrors).length > 0 && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
          <Text type="warning" strong>Some databases could not be loaded:</Text>
          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
            {Object.entries(databaseErrors).map(([dbName, errMsg]) => (
              <li key={dbName}>
                <Text code>{dbName}</Text>: {errMsg}
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading database schemas...</Text>
          </div>
        </div>
      )}

      {!loading && (
        <>
          <AutoComplete
            style={{ width: '100%', marginBottom: 24 }}
            options={autocompleteOptions}
            placeholder="Search for database, table, or field name..."
            onSearch={handleSearch}
            onSelect={handleSelect}
            allowClear
            value={searchValue}
            size="large"
          />

      {selectedResult && (
        <div style={{ 
          marginBottom: 24, 
          padding: 16, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                {selectedResult.type === 'database' && <DatabaseOutlined style={{ fontSize: 20 }} />}
                {selectedResult.type === 'table' && <TableOutlined style={{ fontSize: 20 }} />}
                {selectedResult.type === 'field' && <FieldBinaryOutlined style={{ fontSize: 20 }} />}
                <Title level={4} style={{ margin: 0 }}>
                  {selectedResult.display}
                </Title>
                {selectedResult.type === 'field' && selectedResult.isPrimaryKey && (
                  <Tag color="gold">Primary Key</Tag>
                )}
              </Space>
              <Space>
                {selectedResult.type === 'table' && (
                  <Space>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'table_name',
                            label: 'Copy table name',
                            onClick: () => copyToClipboard(selectedResult.display, 'Table name copied!')
                          },
                          {
                            key: 'table_structure',
                            label: 'Copy table structure (DESCRIBE)',
                            onClick: () => {
                              const structure = generateTableStructure(selectedResult);
                              copyToClipboard(structure, 'Table structure copied!');
                            }
                          }
                        ]
                      }}
                      trigger={['click']}
                    >
                      <Button type="primary" icon={<CopyOutlined />} size="small">
                        Copy <DownOutlined />
                      </Button>
                    </Dropdown>
                    {/* DAS-1844: Sync button for table */}
                    {/* DAS-1853: Preview button */}
                    {/* DAS-1844: Compare PROD<>DEV button - only for tables */}
                    {/* DAS-2326: hidden on PROD — compare must be initiated from DEV
                        (PROD cannot SSH to DEV behind NAT; comparing PROD to itself is noise) */}
                    {!isProd && (
                      <Button
                        type="dashed"
                        icon={<SwapOutlined />}
                        loading={tableCompareLoading}
                        size="small"
                        onClick={handleTableCompare}
                      >
                        Compare PROD↔DEV
                      </Button>
                    )}
                    {canSync && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'preview_to_prod',
                              label: '🔍 Preview DEV → PROD',
                              onClick: () => handlePreviewClick('dev_to_prod')
                            },
                            {
                              key: 'preview_from_prod',
                              label: '🔍 Preview PROD → DEV',
                              onClick: () => handlePreviewClick('prod_to_dev')
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button 
                          type="dashed" 
                          icon={<SwapOutlined />}
                          loading={comparisonLoading}
                          size="small"
                        >
                          Preview
                        </Button>
                      </Dropdown>
                    )}
                    {canSync ? (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'to_prod',
                              label: '⬆️ Push to PROD (DEV → PROD)',
                              onClick: () => handleSyncClick('dev_to_prod')
                            },
                            {
                              key: 'from_prod',
                              label: '⬇️ Pull from PROD (PROD → DEV)',
                              onClick: () => handleSyncClick('prod_to_dev')
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button 
                          type="default" 
                          icon={<CloudSyncOutlined />}
                          loading={syncInProgress}
                          size="small"
                        >
                          Sync <DownOutlined />
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button 
                        type="default" 
                        icon={<CloudSyncOutlined />}
                        disabled
                        size="small"
                        title="Sync must be initiated from DEV (PROD cannot reach DEV)"
                      >
                        Sync from DEV only
                      </Button>
                    )}
                  </Space>
                )}
                {selectedResult.type === 'field' && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'field_name',
                          label: 'Copy field name',
                          onClick: () => copyToClipboard(selectedResult.display, 'Field name copied!')
                        },
                        {
                          key: 'field_full',
                          label: 'Copy field + database + table + type',
                          onClick: () => {
                            const description = generateFieldDescription(selectedResult);
                            copyToClipboard(description, 'Field description copied!');
                          }
                        }
                      ]
                    }}
                    trigger={['click']}
                  >
                    <Button type="primary" icon={<CopyOutlined />} size="small">
                      Copy <DownOutlined />
                    </Button>
                  </Dropdown>
                )}
                {selectedResult.type === 'database' && (
                  <Space>
                    <CopyOutlined
                      onClick={() => copyToClipboard(selectedResult.fullPath, 'Database name copied!')}
                      style={{
                        fontSize: 18,
                        cursor: 'pointer',
                        color: '#1890ff',
                      }}
                      title="Copy database name to clipboard"
                    />
                    {/* DAS-1844: Sync button for database */}
                    {/* DAS-1853: Preview button for database */}
                    {canSync && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'preview_to_prod',
                              label: '🔍 Preview DEV → PROD',
                              onClick: () => handlePreviewClick('dev_to_prod')
                            },
                            {
                              key: 'preview_from_prod',
                              label: '🔍 Preview PROD → DEV',
                              onClick: () => handlePreviewClick('prod_to_dev')
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button 
                          type="dashed" 
                          icon={<SwapOutlined />}
                          loading={comparisonLoading}
                        >
                          Preview
                        </Button>
                      </Dropdown>
                    )}
                    {canSync ? (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'to_prod',
                              label: '⬆️ Push to PROD (DEV → PROD)',
                              onClick: () => handleSyncClick('dev_to_prod')
                            },
                            {
                              key: 'from_prod',
                              label: '⬇️ Pull from PROD (PROD → DEV)',
                              onClick: () => handleSyncClick('prod_to_dev')
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button 
                          type="default" 
                          icon={<CloudSyncOutlined />}
                          loading={syncInProgress}
                        >
                          Sync <DownOutlined />
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button 
                        type="default" 
                        icon={<CloudSyncOutlined />}
                        disabled
                        title="Sync must be initiated from DEV (PROD cannot reach DEV)"
                      >
                        Sync from DEV only
                      </Button>
                    )}
                  </Space>
                )}
              </Space>
            </div>
            <Text type="secondary" code style={{ fontSize: 12 }}>
              {selectedResult.fullPath}
            </Text>
            {selectedResult.type === 'field' && (
              <div>
                <Text type="secondary">Database: </Text>
                <Text code>{selectedResult.databaseName}</Text>
                <Text type="secondary" style={{ marginLeft: 16 }}>Table: </Text>
                <Text code>{selectedResult.tableName}</Text>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Type: </Text>
                  <Text code>{selectedResult.fieldType}</Text>
                  {selectedResult.isPrimaryKey && (
                    <>
                      <Text type="secondary" style={{ marginLeft: 16 }}>Primary Key: </Text>
                      <Text strong>Yes</Text>
                    </>
                  )}
                </div>
              </div>
            )}
            {selectedResult.type === 'table' && (
              <div>
                <Text type="secondary">Database: </Text>
                <Text code>{selectedResult.databaseName}</Text>
              </div>
            )}
          </Space>
        </div>
      )}

      {/* DAS-1844: Sync Progress Indicator */}
      {(syncInProgress || syncStage === 'completed' || syncStage === 'failed') && (
        <div style={{ 
          marginBottom: 24, 
          padding: 16, 
          backgroundColor: syncStage === 'failed' ? '#fff2f0' : syncStage === 'completed' ? '#f6ffed' : '#e6f7ff', 
          borderRadius: 4,
          border: `1px solid ${syncStage === 'failed' ? '#ffccc7' : syncStage === 'completed' ? '#b7eb8f' : '#91d5ff'}`
        }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CloudSyncOutlined style={{ fontSize: 24, color: syncStage === 'failed' ? '#ff4d4f' : syncStage === 'completed' ? '#52c41a' : '#1890ff' }} />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {syncStage === 'completed' ? 'Sync Completed!' : syncStage === 'failed' ? 'Sync Failed' : 'Syncing...'}
                </Title>
                <Text type="secondary">
                  {syncDirectionDisplay.source} → {syncDirectionDisplay.dest}
                </Text>
              </div>
            </div>
            
            {syncInProgress && (
              <Progress percent={syncProgress} status="active" />
            )}
            
            {syncStage === 'completed' && (
              <Alert type="success" message="Data has been successfully copied to the target environment." showIcon />
            )}
            
            {syncStage === 'failed' && syncError && (
              <Alert type="error" message={syncError} showIcon />
            )}
            
            {(syncStage === 'completed' || syncStage === 'failed') && (
              <Button 
                size="small" 
                onClick={() => { setSyncStage(''); setSyncError(null); setSyncProgress(0); }}
              >
                Dismiss
              </Button>
            )}
          </Space>
        </div>
      )}

      {searchValue && !selectedResult && (
        <div>
          <Title level={5}>Search Results ({filteredItems.length})</Title>
          
          {displayResults.database.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Databases:</Text>
              <List
                size="small"
                dataSource={displayResults.database}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: '8px 0', cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedResult(item);
                      setSearchValue(item.fullPath);
                    }}
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <DatabaseOutlined />
                        <Text>{item.display}</Text>
                        <Text type="secondary" code style={{ fontSize: 11 }}>
                          {item.fullPath}
                        </Text>
                      </Space>
                      <CopyOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.fullPath);
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                      />
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          )}

          {displayResults.table.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Tables:</Text>
              <List
                size="small"
                dataSource={displayResults.table}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: '8px 0', cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedResult(item);
                      setSearchValue(item.fullPath);
                    }}
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <TableOutlined />
                        <Text>{item.display}</Text>
                        <Text type="secondary" code style={{ fontSize: 11 }}>
                          {item.fullPath}
                        </Text>
                      </Space>
                      <CopyOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.fullPath);
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                      />
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          )}

          {displayResults.field.length > 0 && (
            <div>
              <Text strong>Fields:</Text>
              <List
                size="small"
                dataSource={displayResults.field}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: '8px 0', cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedResult(item);
                      setSearchValue(item.fullPath);
                    }}
                  >
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <FieldBinaryOutlined />
                        <Text>{item.display}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          ({item.databaseName}.{item.tableName})
                        </Text>
                        {item.isPrimaryKey && (
                          <Tag color="gold" size="small">PK</Tag>
                        )}
                        <Text type="secondary" code style={{ fontSize: 11 }}>
                          {item.fullPath}
                        </Text>
                      </Space>
                      <CopyOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(item.fullPath);
                        }}
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                      />
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          )}

          {filteredItems.length === 0 && (
            <Text type="secondary">No results found for "{searchValue}"</Text>
          )}
        </div>
      )}

      {!searchValue && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <DatabaseOutlined style={{ fontSize: 48, marginBottom: 16 }} />
          <div>
            <Text type="secondary">
              Enter a database, table, or field name to search
            </Text>
          </div>
        </div>
      )}

      {/* DAS-1853: Pre-sync comparison modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            <span>Sync Preview: {comparisonData?.tables?.[0]?.name?.split('.')[0] || 'Database'}</span>
          </Space>
        }
        open={showComparison}
        onCancel={() => { setShowComparison(false); setComparisonData(null); }}
        width={900}
        footer={comparisonData && !comparisonLoading ? [
          <Button key="cancel" onClick={() => { setShowComparison(false); setComparisonData(null); }}>
            Close
          </Button>,
          comparisonData.needs_sync ? (
            <Button 
              key="sync" 
              type="primary" 
              icon={<CloudSyncOutlined />}
              onClick={() => {
                setShowComparison(false);
                handleSyncClick(comparisonData.direction);
              }}
            >
              Proceed with Sync
            </Button>
          ) : (
            <Button key="no-sync" type="primary" disabled>
              No Sync Needed
            </Button>
          )
        ] : null}
      >
        {comparisonLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Comparing databases...</Text>
            </div>
          </div>
        ) : comparisonData ? (
          <div>
            <Alert 
              type={comparisonData.needs_sync ? 'warning' : 'success'}
              message={comparisonData.summary}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Tag>{comparisonData.totals?.source_size || '-'} source</Tag>
                <Tag>{comparisonData.totals?.target_size || '-'} target</Tag>
                {comparisonData.needs_sync && (
                  <Tag color="blue">{comparisonData.totals?.sync_size || '-'} to transfer</Tag>
                )}
              </Space>
            </div>

            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Table</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Source Rows</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Target Rows</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Src Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Tgt Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.tables?.map((t, i) => (
                    <tr key={i} style={{ 
                      borderBottom: '1px solid #e8e8e8',
                      backgroundColor: t.action === 'SKIP' ? '#fafafa' : t.action === 'CREATE' ? '#fff2f0' : '#fffbe6'
                    }}>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{t.name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{t.source_rows?.toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{t.target_rows?.toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{t.source_date}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{t.target_date}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16, borderTop: '1px solid #e8e8e8', paddingTop: 12 }}>
              <Text strong>Totals: </Text>
              <Text>{comparisonData.totals?.source_rows?.toLocaleString()} source → {comparisonData.totals?.target_rows?.toLocaleString()} target</Text>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* DAS-1844: Table Comparison Modal (simple PROD<>DEV comparison) */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            <span>Compare Table: {selectedResult?.databaseName}.{selectedResult?.display}</span>
          </Space>
        }
        open={showTableCompare}
        onCancel={() => { setShowTableCompare(false); setTableCompareData(null); }}
        width={600}
        footer={[
          <Button key="close" onClick={() => { setShowTableCompare(false); setTableCompareData(null); }}>
            Close
          </Button>
        ]}
      >
        {tableCompareLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ marginBottom: 16 }}>
              <div className="ant-spin ant-spin-spinning">
                <span className="ant-spin-dot ant-spin-dot-spin">
                  <i className="ant-spin-dot-item"></i>
                  <i className="ant-spin-dot-item"></i>
                  <i className="ant-spin-dot-item"></i>
                  <i className="ant-spin-dot-item"></i>
                </span>
              </div>
            </div>
            <div>Comparing table between PROD and DEV...</div>
          </div>
        ) : tableCompareData ? (
          <div>
            <Alert 
              type={tableCompareData.match ? 'success' : 'warning'}
              message={tableCompareData.match ? 'Tables are in sync!' : 'Tables differ between PROD and DEV'}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Metric</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#1890ff' }}>DEV</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#52c41a' }}>PROD</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                  <td style={{ padding: '12px', fontWeight: 500 }}>Row Count</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.dev?.rows?.toLocaleString() || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.prod?.rows?.toLocaleString() || '-'}
                  </td>
                  <td style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    color: tableCompareData.diff?.rows === 0 ? '#52c41a' : '#cf1322',
                    fontWeight: 500
                  }}>
                    {tableCompareData.diff?.rows > 0 ? '+' : ''}{tableCompareData.diff?.rows?.toLocaleString() || '0'}
                  </td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                  <td style={{ padding: '12px', fontWeight: 500 }}>Data Size</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.dev?.size || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.prod?.size || '-'}
                  </td>
                  <td style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    color: tableCompareData.diff?.size_bytes === 0 ? '#52c41a' : '#faad14',
                    fontWeight: 500
                  }}>
                    {tableCompareData.diff?.size || '0'}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px', fontWeight: 500 }}>Index Size</td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.dev?.index_size || '-'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {tableCompareData.prod?.index_size || '-'}
                  </td>
                  <td style={{ 
                    padding: '12px', 
                    textAlign: 'center',
                    color: tableCompareData.diff?.index_bytes === 0 ? '#52c41a' : '#faad14',
                    fontWeight: 500
                  }}>
                    {tableCompareData.diff?.index || '0'}
                  </td>
                </tr>
              </tbody>
            </table>

            {tableCompareData.dev?.exists === false && (
              <Alert 
                type="error"
                message="Table does not exist on DEV"
                style={{ marginTop: 16 }}
              />
            )}
            {tableCompareData.prod?.exists === false && (
              <Alert 
                type="error"
                message="Table does not exist on PROD"
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        ) : null}
      </Modal>
        </>
      )}
    </Modal>
  );
};

export default DatabaseSchemaSearch;

